import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AGING_BUCKET_LABELS,
  formatCents,
  formatDate,
  JOB_STATUS_LABELS,
  type JobStatus,
} from '@fsm/shared';
import { api } from '../lib/api';
import { keys } from '../lib/queries';
import { Badge, Card, FilterTabs, PageHeader, Spinner, Table, Td, Th } from '../components/ui';

/*
 * Charts are hand-drawn SVG rather than a charting library: three simple forms,
 * no dependency, and full control over the accessibility story (every bar is
 * directly labelled, and each chart has a table view underneath or beside it).
 *
 * Series colours come from a validated categorical pair; the status chart uses
 * the reserved status palette and labels every bar, so identity is never
 * carried by colour alone.
 */
const SERIES = {
  // Tuned to the Crewlark palette: a slate blue against the accent green.
  // Two greens would have been prettier and unreadable — the pair has to
  // separate by hue, because the two bars are read side by side.
  invoiced: '#4a7f9e',
  collected: '#225d4d',
};

/* The reserved status palette, as declared in the stylesheet's @theme. */
const STATUS_COLOR: Record<JobStatus, string> = {
  unscheduled: '#6b756f',
  scheduled: '#2f6b8f',
  in_progress: '#96601b',
  completed: '#2f6b4f',
  invoiced: '#6b5296',
  paid: '#326846',
  cancelled: '#a8443a',
};

/** Sequential ramp: one hue, monotonically darker as the debt gets older. */
const AGING_RAMP = ['#f3cfc9', '#e7a79e', '#d57d70', '#bc5546', '#9a3628'];

export function ReportsPage() {
  const [months, setMonths] = useState(12);

  const revenue = useQuery({
    queryKey: keys.reports.revenue(months),
    queryFn: () => api.reports.revenue(months),
  });
  const byStatus = useQuery({
    queryKey: keys.reports.jobsByStatus,
    queryFn: () => api.reports.jobsByStatus(),
  });
  const receivables = useQuery({
    queryKey: keys.reports.receivables,
    queryFn: () => api.reports.receivables(),
  });

  const loading = revenue.isLoading || byStatus.isLoading || receivables.isLoading;

  return (
    <>
      <PageHeader
        eyebrow="Business"
        title="Reports"
        subtitle="What you billed, what you collected, and what is still owed"
        actions={
          <FilterTabs
            value={String(months)}
            onChange={(next) => setMonths(Number(next))}
            options={[6, 12, 24].map((m) => ({ value: String(m), label: `${m} mo` }))}
          />
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-[30px]">
        {loading ? (
          <div className="grid place-items-center py-24">
            <Spinner className="size-6 text-ink-4" />
          </div>
        ) : (
          <div className="mx-auto max-w-[1860px] space-y-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <StatTile
                label="Invoiced"
                value={formatCents(revenue.data?.totalInvoicedCents ?? 0)}
                caption={`across the last ${months} months`}
              />
              <StatTile
                label="Collected"
                value={formatCents(revenue.data?.totalCollectedCents ?? 0)}
                caption="money actually received"
                accent={SERIES.collected}
              />
              <StatTile
                label="Outstanding"
                value={formatCents(receivables.data?.totalOutstandingCents ?? 0)}
                caption={`${receivables.data?.invoices.length ?? 0} open invoices`}
                accent="#b3261e"
              />
            </div>

            <Card title="Revenue by month">
              <RevenueChart data={revenue.data?.months ?? []} />
            </Card>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <Card title="Jobs by status">
                <StatusChart counts={byStatus.data?.counts ?? []} total={byStatus.data?.total ?? 0} />
              </Card>

              <Card title="Receivables aging">
                <AgingChart buckets={receivables.data?.buckets ?? []} />
              </Card>
            </div>

            <Card title="Outstanding invoices" bodyClassName="p-0">
              {(receivables.data?.invoices.length ?? 0) === 0 ? (
                <p className="px-4 py-10 text-center text-[13px] text-ink-4">
                  Nothing outstanding. Everything billed has been paid.
                </p>
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th className="w-[70px]">#</Th>
                      <Th>Customer</Th>
                      <Th>Due</Th>
                      <Th align="right">Overdue</Th>
                      <Th align="right">Balance</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {(receivables.data?.invoices ?? []).map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-raised">
                        <Td className="tnum text-ink-4">
                          <Link to={`/invoices/${invoice.id}`} className="hover:text-accent">
                            {invoice.number}
                          </Link>
                        </Td>
                        <Td className="font-medium">{invoice.customerName}</Td>
                        <Td className="tnum">
                          {invoice.dueAt ? formatDate(invoice.dueAt) : '—'}
                        </Td>
                        <Td align="right">
                          {invoice.daysOverdue > 0 ? (
                            <Badge tone={invoice.daysOverdue > 60 ? 'red' : 'amber'}>
                              {invoice.daysOverdue} days
                            </Badge>
                          ) : (
                            <span className="text-ink-4">Current</span>
                          )}
                        </Td>
                        <Td align="right" className="tnum font-semibold">
                          {formatCents(invoice.balanceCents)}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          </div>
        )}
      </div>
    </>
  );
}

function StatTile({
  label,
  value,
  caption,
  accent,
}: {
  label: string;
  value: string;
  caption: string;
  accent?: string;
}) {
  return (
    <div className="rounded-card border border-line bg-surface p-4">
      <div className="flex items-center gap-1.5">
        {accent && (
          <span className="size-2 rounded-full" style={{ background: accent }} aria-hidden />
        )}
        <span className="text-[11px] font-semibold tracking-[0.15em] text-ink-4 uppercase">
          {label}
        </span>
      </div>
      <div className="mt-1.5 font-display text-[28px] leading-none font-extrabold tracking-[-0.035em] tnum">
        {value}
      </div>
      <div className="mt-1.5 text-[12.5px] text-ink-3">{caption}</div>
    </div>
  );
}

interface MonthRow {
  month: string;
  invoicedCents: number;
  collectedCents: number;
  jobCount: number;
}

/**
 * Grouped bars: invoiced beside collected. Two series of the same unit share
 * one axis — never two scales.
 */
function RevenueChart({ data: raw }: { data: MonthRow[] }) {
  const [hover, setHover] = useState<number | null>(null);

  // A business three months old should not be shown nine empty columns. Drop
  // leading months with no activity, but never fall below a six-month frame so
  // the shape of the chart stays stable week to week.
  const data = useMemo(() => {
    const firstActive = raw.findIndex((m) => m.invoicedCents > 0 || m.collectedCents > 0);
    if (firstActive <= 0) return raw;
    return raw.slice(Math.min(firstActive, Math.max(raw.length - 6, 0)));
  }, [raw]);

  const max = useMemo(
    () => Math.max(1, ...data.flatMap((d) => [d.invoicedCents, d.collectedCents])),
    [data],
  );

  const H = 180;
  const groupWidth = 100 / Math.max(data.length, 1);

  const ticks = useMemo(() => {
    const step = niceStep(max / 4);
    const out: number[] = [];
    for (let v = 0; v <= max * 1.02; v += step) out.push(v);
    return out;
  }, [max]);

  if (data.length === 0) {
    return <p className="py-10 text-center text-[13px] text-ink-4">No revenue in this window.</p>;
  }

  return (
    <figure className="m-0">
      <figcaption className="mb-3 flex items-center gap-4 text-[12.5px] text-ink-2">
        <Legend color={SERIES.invoiced} label="Invoiced" />
        <Legend color={SERIES.collected} label="Collected" />
      </figcaption>

      <div className="relative">
        {/* Value axis */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0"
          style={{ height: H }}
          aria-hidden
        >
          {ticks.map((tick) => (
            <div
              key={tick}
              className="absolute right-0 left-0 border-t border-line"
              style={{ bottom: (tick / max) * H }}
            >
              <span className="absolute -top-2 left-0 bg-surface pr-1 text-[10.5px] tnum text-ink-4">
                {tick === 0 ? '0' : formatCompact(tick)}
              </span>
            </div>
          ))}
        </div>

        <svg
          viewBox={`0 0 100 ${H}`}
          preserveAspectRatio="none"
          className="relative block w-full"
          style={{ height: H }}
          role="img"
          aria-label="Invoiced and collected revenue by month"
        >
          {data.map((row, i) => {
            const x = i * groupWidth;
            const barW = groupWidth * 0.3;
            const gap = groupWidth * 0.06;
            const invoicedH = (row.invoicedCents / max) * H;
            const collectedH = (row.collectedCents / max) * H;
            const active = hover === i;

            return (
              <g
                key={row.month}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              >
                {/* Generous hit target, wider than the marks themselves. */}
                <rect x={x} y={0} width={groupWidth} height={H} fill="transparent" />
                {active && (
                  <rect x={x} y={0} width={groupWidth} height={H} fill="#202b32" opacity={0.04} />
                )}
                <rect
                  x={x + groupWidth / 2 - barW - gap / 2}
                  y={H - invoicedH}
                  width={barW}
                  height={invoicedH}
                  rx={1.2}
                  fill={SERIES.invoiced}
                  opacity={hover === null || active ? 1 : 0.45}
                />
                <rect
                  x={x + groupWidth / 2 + gap / 2}
                  y={H - collectedH}
                  width={barW}
                  height={collectedH}
                  rx={1.2}
                  fill={SERIES.collected}
                  opacity={hover === null || active ? 1 : 0.45}
                />
              </g>
            );
          })}
          <line x1={0} y1={H} x2={100} y2={H} stroke="#d0d6df" strokeWidth={1} vectorEffect="non-scaling-stroke" />
        </svg>

        {hover !== null && data[hover] && (
          <div
            className="pointer-events-none absolute -top-1 z-10 -translate-x-1/2 -translate-y-full rounded-lg bg-ink px-2.5 py-2 text-[12px] whitespace-nowrap text-white shadow-lg"
            style={{ left: `${(hover + 0.5) * groupWidth}%` }}
          >
            <div className="font-semibold">{monthLabel(data[hover]!.month)}</div>
            <div className="mt-1 flex items-center gap-1.5">
              <span className="size-2 rounded-full" style={{ background: SERIES.invoiced }} />
              Invoiced <span className="tnum">{formatCents(data[hover]!.invoicedCents)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="size-2 rounded-full" style={{ background: SERIES.collected }} />
              Collected <span className="tnum">{formatCents(data[hover]!.collectedCents)}</span>
            </div>
            <div className="mt-0.5 text-ink-4">{data[hover]!.jobCount} invoices</div>
          </div>
        )}
      </div>

      <div className="mt-1.5 flex text-[10.5px] text-ink-4">
        {data.map((row) => (
          <span
            key={row.month}
            className="text-center"
            style={{ width: `${groupWidth}%` }}
            title={monthLabel(row.month)}
          >
            {shortMonth(row.month)}
          </span>
        ))}
      </div>
    </figure>
  );
}

/** Horizontal bars, every one directly labelled — colour is never the only cue. */
function StatusChart({
  counts,
  total,
}: {
  counts: Array<{ status: JobStatus; count: number }>;
  total: number;
}) {
  const max = Math.max(1, ...counts.map((c) => c.count));

  return (
    <ul className="space-y-2">
      {counts.map((row) => (
        <li key={row.status} className="flex items-center gap-3">
          <span className="w-[92px] shrink-0 text-[12.5px] text-ink-2">
            {JOB_STATUS_LABELS[row.status]}
          </span>
          <div className="h-[18px] min-w-0 flex-1 overflow-hidden rounded-[3px] bg-canvas">
            <div
              className="h-full rounded-[3px]"
              style={{
                width: `${(row.count / max) * 100}%`,
                background: STATUS_COLOR[row.status],
                minWidth: row.count > 0 ? 3 : 0,
              }}
            />
          </div>
          <span className="w-[54px] shrink-0 text-right text-[12.5px] tnum">
            {row.count}
            <span className="ml-1 text-ink-4">
              {total > 0 ? `${Math.round((row.count / total) * 100)}%` : ''}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Sequential single-hue ramp: the older the debt, the darker the bar. */
function AgingChart({
  buckets,
}: {
  buckets: Array<{ bucket: keyof typeof AGING_BUCKET_LABELS; count: number; totalCents: number }>;
}) {
  const max = Math.max(1, ...buckets.map((b) => b.totalCents));

  return (
    <ul className="space-y-2">
      {buckets.map((row, i) => (
        <li key={row.bucket} className="flex items-center gap-3">
          <span className="w-[76px] shrink-0 text-[12.5px] text-ink-2">
            {AGING_BUCKET_LABELS[row.bucket]}
          </span>
          <div className="h-[18px] min-w-0 flex-1 overflow-hidden rounded-[3px] bg-canvas">
            <div
              className="h-full rounded-[3px]"
              style={{
                width: `${(row.totalCents / max) * 100}%`,
                background: AGING_RAMP[i] ?? AGING_RAMP.at(-1),
                minWidth: row.totalCents > 0 ? 3 : 0,
              }}
            />
          </div>
          <span className="w-[104px] shrink-0 text-right text-[12.5px] tnum">
            {row.totalCents > 0 ? formatCents(row.totalCents) : '—'}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="size-2.5 rounded-[2px]" style={{ background: color }} aria-hidden />
      {label}
    </span>
  );
}

function niceStep(raw: number): number {
  const magnitude = Math.pow(10, Math.floor(Math.log10(Math.max(raw, 1))));
  const normalized = raw / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

function formatCompact(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1000) return `$${(dollars / 1000).toFixed(dollars >= 10_000 ? 0 : 1)}k`;
  return `$${Math.round(dollars)}`;
}

function monthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(
    new Date(y ?? 2026, (m ?? 1) - 1, 1),
  );
}

function shortMonth(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', { month: 'short' }).format(
    new Date(y ?? 2026, (m ?? 1) - 1, 1),
  );
}
